import React, { useState, useEffect, useCallback, useRef } from 'react';
import Immutable from 'immutable';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { Node } from '@uncinc/react-drupal-core';

import Modal from '../Modal';
import { popupsLoad } from '../../actions/PopupAnnouncement';

import './style.scss';
import { DEFAULT_POPUP_BANNER_EXPIRATION_DAYS } from '../../config/constants';

const removeEventListeners = () => {
  if (window.popupAnnouncementEventTriggers) {
    window.popupAnnouncementEventTriggers.forEach((trigger) => {
      if (trigger.element === 'document') {
        window.document.removeEventListener(trigger.type, trigger.method, false);
      } else if (trigger.element === 'window') {
        window.removeEventListener(trigger.type, trigger.method, false);
      }
    });
  }

  window.popupAnnouncementEventTriggers = [];
};

const PopupAnnouncement = () => {
  const dispatch = useDispatch();
  const [currentPopup, setCurrentPopup] = useState(null);
  const [currentPageEntityType, setCurrentPageEntityType] = useState(null);

  const [readyToLoadPopup, setReadyToLoadPopup] = useState(false);
  const [visible, setVisible] = useState(false);
  const currentUrl = useSelector((state) => {
    return state.getIn(['router', 'location', 'pathname']);
  });
  const node = useSelector((state) => state.getIn(['node']));
  const currentPageEntityId = useSelector((state) => state.getIn(['entity', 'entityId'], null));

  const popupNodeLoaded = useSelector((state) => Boolean(state.getIn(['node', currentPopup?.get('field_popup_contents')], null)));
  const popups = useSelector((state) => state.getIn(['popupAnnouncement', 'popups'], Immutable.List()));

  // set initial showPopup boolean
  const [showPopup, setShowPopup] = useState(false);

  // Clean up eventlisteners when component loses scope
  useEffect(() => {
    return () => {
      removeEventListeners();
    }
  }, [])

  useEffect(() => {
    // This useEffect is needed to make sure current page entity type is reset after it has been cleared 
    // When the url changes
    if (currentPageEntityId && node) {
      setCurrentPageEntityType(node.getIn([currentPageEntityId, 'type', 0, 'target_id'], null));
    } else {
      setCurrentPageEntityType(null);
    }
  }, [currentPageEntityId, node]);

  // Load the popups once on mount.
  useEffect(() => {
    window.popupAnnouncementEventTriggers = window.popupAnnouncementEventTriggers || [];
    dispatch(popupsLoad());
  }, [dispatch]);

  
  useEffect(() => {
    // Clear popup
    setCurrentPageEntityType(null);
    removeEventListeners();
    setCurrentPopup(null);
    setShowPopup(false);
    setReadyToLoadPopup(true);
  }, [currentUrl]);

  // Determine the visibility of the popups when loaded.
  useEffect(() => {
    if (readyToLoadPopup && popups && popups.size > 0) {
      popups.forEach((popup) => {
        const contentTypes = popup.get('field_content_type', '')
        .split(',').map((type) => type.trim());
  
        const paths = popup.get('paths', '').split('\r\n');

        // Check visibility of the popup.
        if ((currentPageEntityType && contentTypes.includes(currentPageEntityType)) || paths.includes(currentUrl)) {
          setReadyToLoadPopup(false);
          setCurrentPopup(popup);
        }
      });
    }
  }, [readyToLoadPopup, popups, currentPageEntityType]);

  useEffect(() => {
    if (currentPopup) {
      const globalLabel = currentPopup.get('field_global_label');
      const popupId = globalLabel && globalLabel.length ? globalLabel : currentPopup.get('nid');
      const cookieValue = getCookie(popupId);
      const onVisits = currentPopup.get('onVisits', Immutable.List());

      let newCookieValue = 1;

      if (cookieValue) {
        newCookieValue = Number(cookieValue) + 1
      }

      if (!onVisits || onVisits.includes(`${newCookieValue}`)) {
        handleTriggers(newCookieValue);
      }
    }
  }, [currentPopup]);


  // Whenever the popup is set, fire a delayed state change to display it.
  // Also check if the localStorage showPopup boolean was set to false before
  useEffect(() => {
    if (currentPopup && currentPopup.get('field_popup_contents') && showPopup) {
      const delay = currentPopup.get('delay', 1000);
      setTimeout(() => {
        setVisible(true);
      }, delay);
    }
  }, [currentPopup, showPopup]);

  const updateCookieValue = useCallback((newCookieValue) => {
    const globalLabel = currentPopup.get('field_global_label');
    const popupId = globalLabel && globalLabel.length ? globalLabel : currentPopup.get('nid');
    setCookie(popupId, newCookieValue, Number(currentPopup.get('field_expiration') || DEFAULT_POPUP_BANNER_EXPIRATION_DAYS));
  }, [currentPopup]);

  const scrollEventListener = useCallback((scrollPercentageTrigger) => {
    const scrollTop = window.scrollY;
    const docHeight = document.body.offsetHeight;
    const winHeight = window.innerHeight;
    const scrollPercent = scrollTop / (docHeight - winHeight);
    const scrollPercentRounded = Math.round(scrollPercent * 100);

    if (scrollPercentRounded >= scrollPercentageTrigger) {
      setShowPopup(true);
      return true;
    }
  }, [setShowPopup]);

  const mouseOutListener = useCallback(() => {
    setShowPopup(true);
  }, [setShowPopup]);

  const videoEndedListener = useCallback(() => {
    setShowPopup(true);
  }, [setShowPopup]);

  const handleTriggers = useCallback((newCookieValue) => {
    if (currentPopup) {
      let triggerInPlace = false;
      const scrollPosition = Number(currentPopup.get('field_scroll_position'));
      const onWindowMouseLeave = currentPopup.get('field_mouse_leaves_viewport') === 'true';
      const onVideoEnded = currentPopup.get('field_video_ended') === 'true';
    

      if (scrollPosition !== null && scrollPosition !== undefined && scrollPosition !== 0) {
        triggerInPlace = true;
        const _scrollListener = () => {
          const success = scrollEventListener(scrollPosition);

          if (success) {
            updateCookieValue(newCookieValue);
            removeEventListeners();
          }
        };
  
        window.popupAnnouncementEventTriggers.push({type: 'scroll', element: 'window', method: _scrollListener});
        window.addEventListener('scroll', _scrollListener, false);
      }

      if (onWindowMouseLeave) {
        triggerInPlace = true;

        const _mouseOutListener = () => {
          mouseOutListener();
          updateCookieValue(newCookieValue);
          removeEventListeners();
        };

        window.document.addEventListener('mouseleave', _mouseOutListener, false);
        window.popupAnnouncementEventTriggers.push({type: 'mouseleave', element: 'document', method: _mouseOutListener});
      }
      
      if (onVideoEnded) {
        triggerInPlace = true;

        const _videoEndedListener = () => {
          videoEndedListener();
          updateCookieValue(newCookieValue);
          removeEventListeners();
        };

        window.document.addEventListener('video-ended', _videoEndedListener, false);
        window.popupAnnouncementEventTriggers.push({type: 'video-ended', element: 'document', method: _videoEndedListener});
      }

      if (!triggerInPlace) {
        setShowPopup(true);
        updateCookieValue(newCookieValue);
      }
    }
  }, [currentPopup, setShowPopup])

  // close the popup and set the localStorage showPopup boolean to false
  const closePopup = () => {
    setVisible(false);
  };

  const setCookie = (name = 'global', value = '', expiration = DEFAULT_POPUP_BANNER_EXPIRATION_DAYS) => {
    Cookies.set(`popup-${name}`, JSON.stringify(value), { expires: expiration });
  };

  const getCookie = (name = 'global') => {
    return Cookies.get(`popup-${name}`);
  };

  const nodeId = currentPopup && currentPopup.get('field_popup_contents');

  return (
    <>
      {visible && (
        <Modal hideCloseButton onClose={() => closePopup()} >
          <div className={`PopupAnnouncement ${popupNodeLoaded ? '' : 'hidden'}`}>
            <a
              aria-label="Close button"
              className="PopupAnnouncement__close-button"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                closePopup();
              }}>
              <div className="icon-before icon-plus icon-variant-0 icon-rotate-45" />
            </a>
            {nodeId && <Node id={nodeId} viewMode="popup"/>}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PopupAnnouncement;
